class DynamicPricingPlans extends HTMLElement {
  static requests = new Map();

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.config = this.readConfig();
    if (!this.config) return;

    this.grid = this.querySelector('[data-plans-grid]');
    this.errorNode = this.querySelector('[data-plans-error]');

    if (!this.grid) return;

    this.observeAffiliateState();
    this.load();
  }

  disconnectedCallback() {
    this.affiliateObserver?.disconnect();
    this.affiliateObserver = null;
    this.dataset.initialized = 'false';
  }

  readConfig() {
    const node = this.querySelector('[data-plans-config]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[dynamic-pricing-plans] invalid config JSON:', error);
      return null;
    }
  }

  async load() {
    const data = await DynamicPricingPlans.fetchPlansOnce(
      this.config.apiUrl,
      DynamicPricingPlans.buildParams(this.config.params)
    );

    const plans = DynamicPricingPlans.extractPlans(data);
    const currency = this.extractCurrency(data);

    if (!plans || plans.length === 0) {
      this.showError(this.config.strings?.empty || '');
      return;
    }

    this.renderPlans(plans, currency);
  }

  /**
   * Query params for the pricing-plans proxy.
   * @param {{productId?: string|number, variantId?: string|number, market?: string, unitPriceCents?: string|number}} [raw]
   * @returns {Object<string, string>} Params with unitPrice converted to major units.
   */
  static buildParams(raw = {}) {
    const params = {};

    if (raw.productId) params.productId = String(raw.productId);
    if (raw.variantId) params.variantId = String(raw.variantId);
    if (raw.market) params.market = String(raw.market);

    // Proxy expects unit price in major units (e.g. "199.00"); Shopify prices are cents.
    const cents = Number(raw.unitPriceCents);
    if (Number.isFinite(cents) && cents > 0) {
      params.unitPrice = (cents / 100).toFixed(2);
    }

    return params;
  }

  /**
   * Fetch the pricing plans once per page for a given endpoint + params. The pricing cards and the
   * [three_scan_price] placeholders share the in-flight promise, so the proxy is called once.
   * Not persisted across pages: plans depend on the affiliate/location context, which can change.
   * @param {string} apiUrl - Pricing-plans proxy URL.
   * @param {Object<string, string>} params - Params from buildParams().
   * @returns {Promise<object|null>} Parsed JSON payload, or null on failure.
   */
  static fetchPlansOnce(apiUrl, params) {
    if (!apiUrl) return Promise.resolve(null);

    const key = `${apiUrl}?${new URLSearchParams(params).toString()}`;

    if (!DynamicPricingPlans.requests.has(key)) {
      DynamicPricingPlans.requests.set(key, DynamicPricingPlans.requestPlans(apiUrl, params));
    }

    return DynamicPricingPlans.requests.get(key);
  }

  /**
   * Request plans from the proxy, adding the affiliate context when AffiliateFlow is loaded.
   * @param {string} apiUrl - Pricing-plans proxy URL.
   * @param {Object<string, string>} params - Params from buildParams().
   * @returns {Promise<object|null>} Parsed JSON payload, or null on failure.
   */
  static async requestPlans(apiUrl, params) {
    if (window.AffiliateFlow?.fetchPricingPlans) {
      return window.AffiliateFlow.fetchPricingPlans(apiUrl, params);
    }

    try {
      const url = new URL(apiUrl, window.location.origin);
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '') continue;
        url.searchParams.set(key, String(value));
      }
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('[dynamic-pricing-plans] fetch failed:', error);
      return null;
    }
  }

  /**
   * Pull the plans array out of the proxy payload, whichever envelope it uses.
   * @param {object|object[]|null} data - Parsed proxy payload.
   * @returns {object[]|null} Plans, or null when the payload has none.
   */
  static extractPlans(data) {
    if (!data) return null;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.plans)) return data.plans;
    if (Array.isArray(data.plans?.plans)) return data.plans.plans;
    if (Array.isArray(data.data)) return data.data;
    return null;
  }

  extractCurrency(data) {
    return data?.currency || data?.plans?.currency || this.config.currency || 'USD';
  }

  /**
   * Swap the skeleton slots for the API cards. The grid is the section's shared card row, so it
   * may also hold static Plan cards: only the [data-plans-skeleton] slots are replaced, and the
   * new cards land where the Dynamic pricing plans block sits in the block order.
   * @param {object[]} plans - Plan objects from the API response.
   * @param {string} currency - ISO currency code for formatting.
   */
  renderPlans(plans, currency) {
    const money = DynamicPricingPlans.moneyFormatter(currency);
    const ctaLabel = this.config.ctaLabel || 'Choose this plan';

    // Security: every API-sourced value is escaped inside buildCard() before it reaches innerHTML.
    const template = document.createElement('template');
    template.innerHTML = plans.map((plan) => this.buildCard(plan, money, ctaLabel)).join('');

    const skeletons = this.getSkeletons();
    const firstSlot = skeletons[0];

    // Keep the block selectable in the theme editor once its skeletons are gone.
    const editorBlock = firstSlot?.getAttribute('data-shopify-editor-block');
    if (editorBlock) {
      Array.from(template.content.children).forEach((card) => {
        card.setAttribute('data-shopify-editor-block', editorBlock);
      });
    }

    if (firstSlot) {
      firstSlot.replaceWith(template.content);
    } else {
      this.grid.append(template.content);
    }
    skeletons.slice(1).forEach((skeleton) => skeleton.remove());
    this.grid.setAttribute('aria-busy', 'false');

    this.syncPurchaseVisibility();
  }

  /**
   * Skeleton placeholder cards still in the shared row.
   * @returns {HTMLElement[]} The [data-plans-skeleton] slots, in DOM order.
   */
  getSkeletons() {
    return Array.from(this.grid.querySelectorAll('[data-plans-skeleton]'));
  }

  /**
   * Build one plan card, matching the static Plan block layout in sections/pricing-plans.liquid:
   * badge, name, subtitle, price (+ compare-at), price note, per-scan line, feature bullets, CTA.
   * @param {object} plan - A single plan object from the API response.
   * @param {{format: (value: number) => string}} money - Currency formatter for this response.
   * @param {string} defaultCta - Block-level fallback button label.
   * @returns {string} HTML for one <li> card.
   */
  buildCard(plan, money, defaultCta) {
    const name = this.escape(plan.name || plan.title || '');
    const highlighted = Boolean(plan.highlighted || plan.featured);

    const priceValue = DynamicPricingPlans.normalisePrice(plan.total ?? plan.price);
    const compareValue = DynamicPricingPlans.normalisePrice(plan.compareAtTotal ?? plan.compareAtPrice);

    const priceHtml = priceValue !== null ? money.format(priceValue) : this.escape(plan.priceLabel || '');
    const compareHtml =
      compareValue !== null && priceValue !== null && compareValue > priceValue ? money.format(compareValue) : '';

    // The proxy sends the billing note ("Total, billed once") in `subtitle`, which the design
    // places under the price. So subtitle is promoted to the price note unless the API also sends
    // an explicit priceNote, in which case subtitle keeps its own line under the name.
    const explicitNote = plan.priceNote || plan.note || '';
    const priceNote = this.escape(explicitNote || plan.subtitle || '');
    const subtitle = this.escape(explicitNote ? plan.subtitle || '' : '');

    // "$149 /scan" line. unitPrice is the proxy's own per-unit figure, so no division here.
    const unitValue = DynamicPricingPlans.normalisePrice(plan.unitPrice ?? plan.unit_price);
    const perScanHtml = unitValue !== null ? money.format(unitValue) : '';

    const badge = this.escape(this.resolveBadgeText(plan, money));

    const features = Array.isArray(plan.features) ? plan.features.filter(Boolean) : [];
    const featuresHtml = features.map((feature) => this.buildFeatureHtml(feature)).join('');
    const highlightFeature = plan.highlightFeature || plan.highlight_feature || '';
    const highlightHtml = highlightFeature ? this.buildFeatureHtml(highlightFeature, true) : '';

    const cardBorder = highlighted ? 'border-primary shadow-md' : 'border-gray-2 shadow-sm';
    const badgeClass = highlighted ? 'bg-gradient-primary text-white' : 'bg-gray-1 text-secondary';

    return `
      <li class="dyn-pp__card relative flex flex-col bg-white rounded-2xl p-6 border ${cardBorder}">
        ${
          badge
            ? `<span class="dyn-pp__badge absolute -top-3 left-6 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}">${badge}</span>`
            : ''
        }
        ${name ? `<p class="text-xl font-bold text-secondary">${name}</p>` : ''}
        ${subtitle ? `<p class="text-sm text-gray-7 mt-1">${subtitle}</p>` : ''}
        <div class="dyn-pp__price-row flex items-baseline gap-3 mt-6">
          ${priceHtml ? `<span class="text-[40px] leading-none font-bold text-secondary">${priceHtml}</span>` : ''}
          ${compareHtml ? `<s class="text-base text-gray-6">${compareHtml}</s>` : ''}
        </div>
        ${priceNote ? `<p class="text-sm text-gray-7 mt-2">${priceNote}</p>` : ''}
        ${
          perScanHtml
            ? `<p class="text-sm text-secondary mt-3"><strong>${perScanHtml}</strong> <span class="text-gray-7">/scan</span></p>`
            : ''
        }
        ${
          featuresHtml || highlightHtml
            ? `<ul class="dyn-pp__features mt-6 space-y-3 border-t border-gray-2 pt-6">${featuresHtml}${highlightHtml}</ul>`
            : ''
        }
        <div class="dyn-pp__cta mt-auto pt-8">
          ${this.buildPurchaseHtml(plan, defaultCta)}
        </div>
      </li>
    `;
  }

  /**
   * One feature bullet. Normal features use the same checkmark-icon.svg asset the static Plan
   * cards render; a highlighted bonus line uses the pink inline check, matching that design.
   * @param {string} feature - Feature label text.
   * @param {boolean} [isHighlight] - Render the pink bonus check instead of the standard icon.
   * @returns {string} HTML for one <li> bullet.
   */
  buildFeatureHtml(feature, isHighlight = false) {
    const icon =
      isHighlight || !this.config.checkmarkIconUrl
        ? `<svg class="w-5 h-5 flex-none mt-0.5 text-primary" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
             <path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`
        : `<img src="${this.escape(
            this.config.checkmarkIconUrl
          )}" width="20" height="20" alt="" class="w-5 h-5 flex-none mt-0.5">`;

    return `
      <li class="flex gap-2 items-start text-sm text-gray-8">
        ${icon}
        <span>${this.escape(feature)}</span>
      </li>
    `;
  }

  /**
   * Badge pill text. Uses the API's own badgeText when set; otherwise derives "Save $X" from the
   * savings figure the proxy already returns, so a discounted tier carries the same badge the
   * static Plan cards show via their [plan_savings_total] token.
   * @param {object} plan - A single plan object from the API response.
   * @param {{format: (value: number) => string}} money - Currency formatter for this response.
   * @returns {string} Badge label, or '' when the plan has nothing to advertise.
   */
  resolveBadgeText(plan, money) {
    const explicit = plan.badgeText || plan.badge || '';
    if (explicit) return explicit;

    const savings = DynamicPricingPlans.normalisePrice(plan.savings);
    if (savings !== null && savings > 0) return `Save ${money.format(savings)}`;

    return '';
  }

  /**
   * Build the card's purchase block.
   * @param {object} plan - A single plan object from the API response.
   * @param {string} defaultCta - Block-level fallback button label.
   * @returns {string} HTML for the card's call to action.
   */
  buildPurchaseHtml(plan, defaultCta) {
    const label = this.escape(plan.ctaLabel || defaultCta);
    const ctaUrl = plan.ctaUrl || plan.url || '';

    if (ctaUrl) {
      return `<a href="${this.escape(
        ctaUrl
      )}" class="button-primary-gradient button-lg block w-full text-center">${label}</a>`;
    }

    const variantId = this.resolveVariantId(plan);
    if (!variantId) {
      return `<button type="button" class="button-primary-gradient button-lg block w-full text-center" disabled>${label}</button>`;
    }

    const product = this.config.product || {};
    const cart = this.config.cart || {};
    const strings = this.config.strings || {};

    const sellingPlanId = this.resolveSellingPlanId(plan);
    const quantity = Number(plan.quantity) > 0 ? Number(plan.quantity) : 1;
    const productType = this.escape(product.type || '');

    // Same gating as sections/pricing-plans.liquid + snippets/product-form.liquid: only a
    // subscription product locks out once it is in the cart, because a second membership
    // line item is not purchasable.
    const isSubscription = product.isSubscription === true;
    const isInCart = isSubscription && Boolean(product.inCart);
    const isSubscriptionBlocked =
      isSubscription && (Boolean(cart.hasSellingPlan) || Boolean(cart.isSubscriptionCustomer));

    let buttonLabel;
    if (isInCart) {
      buttonLabel = `<span class="buttun-text text-primary-gradient">${this.escape(
        strings.already_in_cart || ''
      )}</span>`;
    } else if (product.available !== false) {
      buttonLabel = `<span class="buttun-text text-primary-gradient">${label}</span>`;
    } else {
      buttonLabel = `<span class="opacity-20">${this.escape(strings.sold_out || '')}</span>`;
    }

    const buttonState = { buttonLabel, isInCart, isSubscriptionBlocked };
    const hasLocation = this.isLocationSelected();

    // .form-wrapper's data-* attributes are what AffiliateFlow.redirectToLocationPage()
    // reads to build the /pages/locations URL, and what cart.js reads for the quantity.
    const wrapperAttributes = [
      `class="form-wrapper${hasLocation ? '' : ' hidden'}"`,
      `data-product-id="${this.escape(product.id || '')}"`,
      `data-variant-id="${this.escape(variantId)}"`,
      `data-product-type="${productType}"`,
      `data-quantity="${quantity}"`,
    ];
    if (sellingPlanId) {
      wrapperAttributes.push(`data-selling-plan-id="${this.escape(sellingPlanId)}"`);
    }

    return `
      <div class="pricing-plan-form buy-now-button-block">
        ${
          hasLocation
            ? ''
            : `<div class="mockup-button-block">${this.buildAddToCartButton({
                ...buttonState,
                isSubmit: false,
              })}</div>`
        }
        <div ${wrapperAttributes.join(' ')}>
          <form
            method="post"
            action="${this.escape(this.config.cartAddUrl || '/cart/add')}"
            accept-charset="UTF-8"
            class="shopify-product-form"
            enctype="multipart/form-data"
            onsubmit="onSubmitAddToCartForm(this, event)"
          >
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="&#10003;">
            <input type="hidden" name="id" value="${this.escape(variantId)}" data-product-type="${productType}">
            ${sellingPlanId ? `<input type="hidden" name="selling_plan" value="${this.escape(sellingPlanId)}">` : ''}
            <div class="flex">
              ${this.buildAddToCartButton({ ...buttonState, isSubmit: true })}
            </div>
          </form>
        </div>
      </div>
    `;
  }

  /**
   * Render one .btn-atc button. The mockup variant (isSubmit false) is the one affiliate-flow.js
   * intercepts to send the shopper to the locations page; the submit variant posts the product
   * form through cart.js. Disabled states follow snippets/product-form.liquid.
   * @param {object} state - Resolved button state for this card.
   * @returns {string} HTML for a single add-to-cart button.
   */
  buildAddToCartButton({ buttonLabel, isInCart, isSubscriptionBlocked, isSubmit }) {
    const attributes = [
      // Disabled look comes from the :disabled state in input.css (parity with snippets/product-form.liquid).
      'class="btn-atc button-primary-light button-lg w-full flex justify-center items-center gap-4"',
      isSubmit ? 'type="submit" name="add"' : 'type="button"',
      `data-text-add-to-cart="${this.escape(this.config.strings?.add_to_cart || '')}"`,
    ];

    if (isInCart) {
      attributes.push('data-plan-in-cart="true"', 'aria-disabled="true"', 'disabled');
    } else if (!isSubmit && isSubscriptionBlocked) {
      attributes.push('data-selling-plan-in-cart="true"', 'disabled');
    }

    return `<button ${attributes.join(' ')}>${this.spinnerHtml()}${buttonLabel}</button>`;
  }

  /**
   * Inline copy of snippets/white-spinner-icon.liquid. cart.js toggles it via the
   * `.animate-spin` selector; the snippet's id is dropped so repeated cards do not
   * duplicate an element id.
   * @returns {string} HTML for the button spinner.
   */
  spinnerHtml() {
    return `
      <svg aria-hidden="true" role="status" class="hidden w-6 h-6 text-primary-pink animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"></path>
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"></path>
      </svg>
    `;
  }

  /**
   * Variant the card adds to cart: the API's own variant id when present, otherwise the
   * variant of the product configured on the block (the same product the static Plan
   * blocks use).
   * @param {object} plan - A single plan object from the API response.
   * @returns {string} Shopify variant id, or '' when none can be resolved.
   */
  resolveVariantId(plan) {
    return String(plan.variantId || plan.variant_id || this.config.product?.variantId || '');
  }

  /**
   * Selling plan the card adds to cart. Priority: the id the API sent, then an allocation
   * on the configured variant whose name matches the plan's billing interval, then the
   * first allocation when the plan is flagged recurring — the same resolution order
   * snippets/product-form.liquid applies server-side.
   * @param {object} plan - A single plan object from the API response.
   * @returns {string} Shopify selling plan id, or '' for a one-time purchase.
   */
  resolveSellingPlanId(plan) {
    const explicit = plan.sellingPlanId || plan.selling_plan_id;
    if (explicit) return String(explicit);

    const allocations = this.config.product?.sellingPlans || [];
    if (allocations.length === 0) return '';

    const hint = String(
      plan.interval || plan.billingInterval || plan.billing_interval || plan.subscriptionInterval || plan.name || ''
    ).toLowerCase();
    const wantYearly = hint.includes('year') || hint.includes('annual');
    const wantMonthly = hint.includes('month');

    if (wantYearly || wantMonthly) {
      const match = allocations.find((allocation) => {
        const allocationName = String(allocation.name || '').toLowerCase();
        return wantYearly
          ? allocationName.includes('year') || allocationName.includes('annual')
          : allocationName.includes('month');
      });
      if (match) return String(match.id);
    }

    const isRecurring =
      plan.isSubscription === true ||
      plan.subscription === true ||
      plan.recurring === true ||
      wantYearly ||
      wantMonthly;

    return isRecurring ? String(allocations[0].id) : '';
  }

  /**
   * Whether the shopper already has a Styku location scoped to their cart. Mirrors the
   * three signals snippets/product-form.liquid and affiliate-flow.js rely on:
   * server-render snapshot, affiliate-user body class, and AffiliateFlow location id.
   * @returns {boolean} True when the location step can be skipped.
   */
  isLocationSelected() {
    if (this.config.locationSelected) return true;
    if (document.body?.classList.contains('affiliate-user')) return true;
    if (typeof window.AffiliateFlow?.getLocationId === 'function' && window.AffiliateFlow.getLocationId()) return true;
    return false;
  }

  /**
   * Apply the affiliate button swap (hide mockup buttons, reveal the real forms) to cards
   * this component rendered. AffiliateFlow.applyAffiliateUI() runs on DOMContentLoaded and
   * cannot see markup created later by the plans fetch.
   */
  syncPurchaseVisibility() {
    if (!document.body?.classList.contains('affiliate-user')) return;

    this.querySelectorAll('.mockup-button-block').forEach((element) => {
      element.style.display = 'none';
    });
    this.querySelectorAll('.form-wrapper').forEach((element) => {
      element.classList.remove('hidden');
    });
  }

  /**
   * AffiliateFlow adds the `affiliate-user` class asynchronously (it awaits a cart update and
   * the affiliate lookup), which can land after the plans render. Watch for it once, then
   * re-apply the button swap.
   */
  observeAffiliateState() {
    if (!document.body || document.body.classList.contains('affiliate-user')) return;

    this.affiliateObserver = new MutationObserver(() => {
      if (!document.body.classList.contains('affiliate-user')) return;

      this.affiliateObserver.disconnect();
      this.affiliateObserver = null;
      this.syncPurchaseVisibility();
    });

    this.affiliateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Coerce an API price to a number for Intl formatting.
   *
   * The proxy quotes money in MAJOR units, not cents: a 10-pack comes back as
   * `total: 1199` / `compareAtTotal: 1990` meaning $1,199.00 / $1,990.00, and
   * `unitPrice: 149.67` is `449 / 3`. buildParams() likewise sends unitPrice as major
   * units. So the value is used as-is — an earlier "values >= 1000 without decimals are
   * cents" guess rendered the 10-pack as $11.99 instead of $1,199.00.
   * @param {number|string|null} value - Raw price from the API payload.
   * @returns {number|null} The price in major units, or null when not a finite number.
   */
  static normalisePrice(value) {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return num;
  }

  /**
   * Currency formatter that drops a zero decimal part: a whole amount renders as $199 / $1,199,
   * while an amount with cents keeps both digits ($129.40, $149.67). Matches the
   * money_without_trailing_zeros filter the static Plan cards and the cart use.
   * @param {string} currency - ISO currency code from the API or the block config.
   * @returns {{format: (value: number) => string}} Formatter with a money-string format method.
   */
  static moneyFormatter(currency) {
    const build = (digits) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency || 'USD',
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        });
      } catch {
        return null;
      }
    };

    const wholeFormatter = build(0);
    const decimalFormatter = build(2);

    return {
      format(value) {
        const isWhole = Number.isInteger(Number(value));
        const formatter = isWhole ? wholeFormatter : decimalFormatter;
        if (formatter) return formatter.format(value);
        return `$${Number(value).toFixed(isWhole ? 0 : 2)}`;
      },
    };
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Drop the skeleton slots after a failed or empty fetch. The message only shows when the row
   * is left with no cards at all; static Plan cards in the same row keep the section usable.
   * @param {string} message - Translated empty/error text.
   */
  showError(message) {
    this.getSkeletons().forEach((skeleton) => skeleton.remove());
    this.grid.setAttribute('aria-busy', 'false');
    if (!this.errorNode) return;

    const showMessage = Boolean(message) && this.grid.children.length === 0;
    this.errorNode.textContent = showMessage ? message : '';
    this.errorNode.hidden = !showMessage;
  }
}

if (!window.customElements.get('dynamic-pricing-plans')) {
  window.customElements.define('dynamic-pricing-plans', DynamicPricingPlans);
}

/**
 * Hydrate pack-price placeholders (.js-plan-price, rendered by snippets/dynamic-text.liquid for
 * [three_scan_price]) with the total of the API plan whose quantity matches data-quantity. Uses
 * fetchPlansOnce, so a page that also shows the pricing cards makes a single proxy request. On any
 * failure the server-rendered fallback (PBS price x quantity) stays in place.
 * Security: only a numeric total is read from the response, and it is written via textContent.
 * @returns {Promise<void>}
 */
async function hydratePlanPrices() {
  const elements = Array.from(document.querySelectorAll('.js-plan-price'));
  if (elements.length === 0) return;

  await Promise.all(
    elements.map(async (element) => {
      try {
        const { plansApi, quantity, productId, variantId, market, unitPriceCents, currency } = element.dataset;
        const params = DynamicPricingPlans.buildParams({ productId, variantId, market, unitPriceCents });
        const data = await DynamicPricingPlans.fetchPlansOnce(plansApi, params);
        const plans = DynamicPricingPlans.extractPlans(data) || [];

        const plan = plans.find((candidate) => Number(candidate.quantity) === Number(quantity));
        const total = DynamicPricingPlans.normalisePrice(plan?.total ?? plan?.price);
        if (total === null || total <= 0) return;

        const money = DynamicPricingPlans.moneyFormatter(data?.currency || data?.plans?.currency || currency);
        element.textContent = money.format(total);
      } catch (error) {
        console.error('[dynamic-pricing-plans] plan price hydration failed:', error);
      }
    })
  );
}

if (document.readyState !== 'loading') {
  hydratePlanPrices();
} else {
  document.addEventListener('DOMContentLoaded', hydratePlanPrices);
}
